import { useCallback, useEffect, useRef, useState } from "react";

const sameDraft = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);
export const useSavedForm = (read, write, equals = sameDraft) => {
  const [record, setRecord] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const load = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    setLoading(true);
    setError("");
    try {
      const next = await read();
      setRecord(next);
      setDraft(next.draft);
    } catch {
      setError("No se han podido cargar los datos. Vuelve a intentarlo.");
    } finally {
      pending.current = false;
      setLoading(false);
    }
  }, [read]);
  useEffect(() => {
    load();
  }, [load]);
  const dirty = record && !equals(draft, record.draft);
  const save = async (event) => {
    event?.preventDefault();
    if (pending.current || !dirty) return;
    pending.current = true;
    setSaving(true);
    setError("");
    const submitted = draft;
    try {
      const response = await write(submitted, record.raw);
      if (response?.error !== false) throw new Error();
      const confirmed = await read();
      if (!equals(confirmed.draft, submitted)) throw new Error();
      setRecord(confirmed);
      setDraft(confirmed.draft);
    } catch {
      setError(
        "No se ha podido confirmar el guardado. El borrador se conserva; revisa los datos o vuelve a intentarlo.",
      );
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };
  const reset = () => {
    if (!pending.current && record) {
      setDraft(record.draft);
      setError("");
    }
  };
  return {
    draft,
    setDraft,
    record,
    dirty,
    loading,
    saving,
    error,
    load,
    save,
    reset,
  };
};

export const SaveFooter = ({
  dirty,
  saving,
  error,
  reset,
  label = "Guardar",
}) => (
  <>
    {error && (
      <p className="generalFormError" role="alert">
        {error}
      </p>
    )}
    <div className="generalSaveFooter">
      <span role="status">
        {saving
          ? "Guardando…"
          : error
            ? "Guardado sin confirmar"
            : dirty
              ? "Cambios sin guardar"
              : "Todo guardado"}
      </span>
      {dirty && reset && (
        <button
          type="button"
          data-discard="true"
          disabled={saving}
          onClick={reset}
        >
          Descartar cambios
        </button>
      )}
      <button type="submit" disabled={!dirty || saving}>
        {saving ? "Guardando…" : label}
      </button>
    </div>
  </>
);

export const LoadState = ({ form, label }) =>
  form.loading ? (
    <p role="status">Cargando {label}…</p>
  ) : !form.record ? (
    <div>
      <p role="alert">{form.error}</p>
      <button type="button" onClick={form.load}>
        Reintentar {label}
      </button>
    </div>
  ) : null;
